// src/features/links/previewCells.tsx
// IR-1047 — target-preview cells for the Join Links table: target group Name,
// Bio and Picture (resolved on demand), plus the "Fetch preview" button.
//
// Resolution lifecycle is driven by `target_preview.status`:
//   null / "pending" — not yet resolved → show "Fetch preview" (or "—" before
//                        a preview exists, in the Name/Bio/Picture columns).
//   "ok"            — resolved → render name/bio/thumbnail.
//   "unsupported"   — Signal → render "—" (no button; nothing to fetch).
//   "failed"        — render "Retry" in place of "Fetch preview".
//
// `name_en` / `bio_en` are English translations shown in a tooltip — only when
// the `_en` value exists AND differs from the native value.
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DownloadCloud, Loader2, RefreshCw, RotateCcw } from "lucide-react";
import { IconButton } from "./IconButton";
import { highlightMatches, snippetAroundMatch } from "./highlight";
import { targetPreviewPhotoUrl } from "./photoUrl";
import type { TargetPreview } from "./types";

function emDash() {
  return <span className="text-[var(--iris-fg-4)]">—</span>;
}

/** True when an English translation exists and is meaningfully different. */
function hasTranslation(native: string | null, english: string | null): english is string {
  return !!english && english.trim() !== "" && english.trim() !== (native ?? "").trim();
}

// IR-1233 r4 — bio snippet re-centering. The Bio cell clamps a max-w-[22ch]
// block; a match past roughly this many characters sits outside the visible
// window, so nav "scrolled to nothing". 80 is conservative for the 3-line solo
// clamp (spaces + wrapping eat into it).
const BIO_MATCH_VISIBLE_WINDOW = 80;
// IR-1233 r5 — when native and English are shown together each gets 2 lines,
// so the visible window per block is proportionally smaller.
const BIO_PAIRED_VISIBLE_WINDOW = 44;
// How much context to keep before the first occurrence when re-centering.
const BIO_SNIPPET_LEAD_IN = 30;

/**
 * Re-center a long bio on the query's first occurrence so the highlight mark
 * lands inside the line-clamp window. Returns `bio` unchanged when `q` is
 * empty/absent, never occurs, or the first occurrence already starts inside
 * the visible window. Otherwise returns an ellipsis-prefixed snippet starting
 * ~BIO_SNIPPET_LEAD_IN chars before the occurrence, snapped forward to the
 * nearest whitespace (avoid mid-word starts).
 */
export function bioSnippet(
  bio: string,
  q: string | null | undefined,
  visibleWindow: number = BIO_MATCH_VISIBLE_WINDOW,
): string {
  // IR-1233 r6: the re-centering rule now lives in ./highlight next to
  // highlightMatches (the Link cell needs the same treatment); behaviour here
  // is unchanged.
  return snippetAroundMatch(bio, q, { visibleWindow, leadIn: BIO_SNIPPET_LEAD_IN });
}

// IR-1233 r5 — Bio readability. r4 fit the native bio and the revealed English
// into ONE shared 3-line clamp by cutting the native down to ~22 chars, which
// read as "the match is displayed but not the text … the bio is hidden"
// (Rotem, 2026-07-23). Native and English now get their OWN clamp, so both
// keep a real length: 3 lines when the native renders alone, 2 lines each when
// the English is revealed below it. The full values stay hoverable via `title`
// and the tooltip.
const CLAMP_BASE =
  "block overflow-hidden text-ellipsis whitespace-normal break-words [display:-webkit-box] [-webkit-box-orient:vertical]";
/** Native rendering alone — the pre-r5 3-line window, unchanged. */
const CLAMP_SOLO = `${CLAMP_BASE} [-webkit-line-clamp:3]`;
/** Native and English stacked — 2 lines each. */
const CLAMP_PAIRED = `${CLAMP_BASE} [-webkit-line-clamp:2]`;

interface TranslatedTextProps {
  /** Native-language value rendered inline. */
  native: string | null;
  /** English translation, shown in a tooltip when present + different. */
  english: string | null;
  /** Extra classes for the rendered text span (e.g. truncation). */
  className?: string;
  /** Native `title` fallback (e.g. the full untruncated value). */
  title?: string | undefined;
  /** IR-1233 r3 — active search query; occurrences in the NATIVE value get
   *  marked. IR-1233 r4: when the query hits the ENGLISH value but not the
   *  native one (the backend searches `_en` too), the English is additionally
   *  rendered inline as a secondary line, marks included, so useMatchNav
   *  registers the row (it was tooltip-only → zero visible marks → nav skips). */
  highlightQuery?: string | undefined;
  /** IR-1233 r4 — route a revealed English value through bioSnippet (Bio cell:
   *  bio_en can be long and the same line-clamp applies via the parent block). */
  snippetEnglish?: boolean;
  /** IR-1233 r5 — Bio cell: clamp the native and the revealed English in their
   *  OWN blocks (3 lines solo, 2 lines each when stacked) instead of sharing
   *  one clamp window. The Name cell renders unclamped and passes nothing. */
  clampBlocks?: boolean;
}

/**
 * Render a native-language value, attaching an English-translation tooltip when
 * an `_en` value exists and differs. Falls back to an em-dash when empty.
 */
function TranslatedText({
  native,
  english,
  className,
  title,
  highlightQuery,
  snippetEnglish,
  clampBlocks,
}: TranslatedTextProps) {
  if (!native || native.trim() === "") return emDash();
  /** Wrap a value in its own clamp block (Bio); render bare on the Name path. */
  const block = (content: React.ReactNode, clamp: string) =>
    clampBlocks ? <span className={clamp}>{content}</span> : content;
  if (!hasTranslation(native, english)) {
    return (
      <span className={className} title={title ?? native}>
        {block(highlightMatches(native, highlightQuery), CLAMP_SOLO)}
      </span>
    );
  }
  // IR-1233 r4: reveal the English inline when the query hits it but NOT the
  // native value — otherwise the row's only match lives in a closed tooltip
  // and match nav skips it. When the query (also) matches the native value,
  // render exactly as before.
  const needle = highlightQuery?.trim().toLowerCase();
  const revealEnglish =
    !!needle && english.toLowerCase().includes(needle) && !native.toLowerCase().includes(needle);
  const inlineEnglish = revealEnglish
    ? snippetEnglish
      ? bioSnippet(english, highlightQuery, BIO_PAIRED_VISIBLE_WINDOW)
      : english
    : null;
  // IR-1233 r5 — the native renders in FULL alongside the revealed English
  // (r4 cut it to ~22 chars so both fit one clamp window). Stacked, each gets
  // its own 2-line clamp; the English keeps the secondary — but readable —
  // colour (fg-3, was the much dimmer fg-4).
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={className} title={title ?? native}>
            {block(
              highlightMatches(native, highlightQuery),
              inlineEnglish != null ? CLAMP_PAIRED : CLAMP_SOLO,
            )}
            {inlineEnglish != null && (
              <span
                className={
                  clampBlocks
                    ? `${CLAMP_PAIRED} text-xs text-[var(--iris-fg-3)]`
                    : "block text-xs text-[var(--iris-fg-3)]"
                }
              >
                ({highlightMatches(inlineEnglish, highlightQuery)})
              </span>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <span className="block max-w-[40ch]">{english}</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface PreviewNameCellProps {
  preview: TargetPreview | null | undefined;
  /** IR-1233 r3 — active search query for match highlighting. */
  highlightQuery?: string | undefined;
}

/** Target group Name (+ English-translation tooltip on `name_en`). */
export function PreviewNameCell({ preview, highlightQuery }: PreviewNameCellProps) {
  if (!preview || preview.status === "unsupported") return emDash();
  return (
    <TranslatedText
      native={preview.name}
      english={preview.name_en}
      className="text-xs text-[var(--iris-fg-1)]"
      highlightQuery={highlightQuery}
    />
  );
}

interface PreviewBioCellProps {
  preview: TargetPreview | null | undefined;
  /** IR-1233 r3 — active search query for match highlighting. */
  highlightQuery?: string | undefined;
}

/** Target group Bio, truncated (+ English-translation tooltip on `bio_en`). */
export function PreviewBioCell({ preview, highlightQuery }: PreviewBioCellProps) {
  if (!preview || preview.status === "unsupported") return emDash();
  return (
    <TranslatedText
      // IR-1233 r4: re-center a long bio on the first match so the mark lands
      // inside the line-clamp window; the full bio stays hoverable via title.
      native={preview.bio == null ? null : bioSnippet(preview.bio, highlightQuery)}
      english={preview.bio_en}
      // IR-1213: narrower + wrapping (was max-w-[32ch] truncate) so the Bio
      // column stops dominating the table width. The line clamp now lives on
      // the inner blocks (IR-1233 r5) so the native and a revealed English
      // translation are capped independently; the full text stays available via
      // the title tooltip. IR-1233 r5: fg-3 → fg-2 — the bio was too dim to
      // read against the dark theme ("the text should be whiter", Rotem).
      className="block max-w-[22ch] text-xs text-[var(--iris-fg-2)]"
      title={preview.bio ?? undefined}
      highlightQuery={highlightQuery}
      snippetEnglish
      clampBlocks
    />
  );
}

interface PreviewPictureCellProps {
  preview: TargetPreview | null | undefined;
}

/**
 * Small rounded thumbnail of the target group's photo, built from the GCS
 * object path via `targetPreviewPhotoUrl` (served through the auth-gated
 * /preview-image proxy route). Hovering the thumbnail opens the full-size image
 * in a tooltip. Falls back to an em-dash when there is no photo (or the platform
 * is unsupported).
 */
export function PreviewPictureCell({ preview }: PreviewPictureCellProps) {
  const src = preview ? targetPreviewPhotoUrl(preview.photo_url) : null;
  if (!src) return emDash();
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <img
            src={src}
            alt=""
            loading="lazy"
            className="h-7 w-7 cursor-zoom-in rounded-full border border-[var(--iris-line-2)] object-cover"
          />
        </TooltipTrigger>
        <TooltipContent className="p-1">
          <img
            src={src}
            alt=""
            className="max-h-[320px] max-w-[320px] rounded-[var(--iris-r-sm)] object-contain"
          />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface PreviewMembersCellProps {
  preview: TargetPreview | null | undefined;
}

/**
 * Target group member count (right-aligned, thousands-grouped). Falls back to an
 * em-dash when the platform exposes no public count (e.g. WhatsApp) or the link
 * is unresolved/unsupported. The count is resolved alongside name/bio/picture and
 * lives on `target_preview.member_count`.
 */
export function PreviewMembersCell({ preview }: PreviewMembersCellProps) {
  if (!preview || preview.status === "unsupported" || preview.member_count == null) {
    return emDash();
  }
  return (
    <span className="text-xs tabular-nums text-[var(--iris-fg-1)]">
      {preview.member_count.toLocaleString("en-US")}
    </span>
  );
}

interface FetchPreviewButtonProps {
  preview: TargetPreview | null | undefined;
  onFetch: () => void;
  pending: boolean;
  /**
   * IR-1386 r3 (Rotem) — icon-only, platform-icon sized, for the table's
   * Actions column. The detail panel keeps the labelled button: it has the room,
   * and there the control is the point of the section rather than a per-row
   * affordance competing with 9 other columns.
   */
  compact?: boolean;
}

/**
 * "Fetch preview" affordance. Hidden (renders "—") for status "unsupported"
 * (Signal — nothing to fetch). Shows "Retry" on "failed", "Fetch preview" on
 * unresolved (null/"pending"), and nothing once resolved "ok". Disabled while a
 * resolve mutation is in flight.
 */
export function FetchPreviewButton({
  preview,
  onFetch,
  pending,
  compact,
}: FetchPreviewButtonProps) {
  const status = preview?.status ?? null;

  if (status === "unsupported") return emDash();
  if (status === "ok") {
    // Already resolved — allow a manual re-fetch (refresh) but keep it subtle.
    if (compact) {
      return (
        <IconButton
          icon={RefreshCw}
          label="Refresh preview"
          disabled={pending}
          busy={pending}
          onClick={onFetch}
        />
      );
    }
    return (
      <button
        type="button"
        className="iris-btn text-xs"
        aria-label="Refresh preview"
        disabled={pending}
        onClick={onFetch}
      >
        {pending ? "Fetching…" : "Refresh"}
      </button>
    );
  }

  const label = status === "failed" ? "Retry" : "Fetch preview";
  if (compact) {
    // IR-1386 r3: in the table the glyph replaces the word — but "Retry" was
    // the ONLY thing on the row saying a fetch had been tried and failed (a
    // failed preview leaves the Name/Bio/Picture cells simply empty, which is
    // also what "never fetched" looks like). So the distinction survives twice:
    // a different glyph, and a different accessible name — which is the hover
    // tooltip too, now that there is no caption to read.
    return (
      <IconButton
        icon={pending ? Loader2 : status === "failed" ? RotateCcw : DownloadCloud}
        label={status === "failed" ? "Retry preview fetch" : "Fetch preview"}
        disabled={pending}
        busy={pending}
        onClick={onFetch}
      />
    );
  }
  return (
    <button
      type="button"
      className="iris-btn text-xs"
      aria-label="Fetch preview"
      disabled={pending}
      onClick={onFetch}
    >
      {pending ? "Fetching…" : label}
    </button>
  );
}
